import FilterListIcon from "@mui/icons-material/FilterList";
import { Box, capitalize, Chip, MenuItem, Stack, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import CustomButton from "../Common/CustomButton";
import EllipsisedCell from "../Common/EllipsisedCell";
import { LoggedInSkeleton } from "../Common/LoggedInSkeleton";
import { getReconTopBarButtons } from "../Common/TopNavBar";
import useFetch from "../Common/useFetch";
import { userContext } from "../Contexts/userContext";
import ReactTable from "../ReactTable";
import { AddEditVendor, VendorFormData } from "./AddEditVendor";
import DeleteConfirmationModal from "./DeleteConfirmationModal";
import { downloadExcelFromBuffer } from "./UploadFileCommonDialog";
import VendorDashboardFilter from "./VendorDashboardFilter";

export type AllowedFilterKeysVendor = "vendorName" | "PANNumber" | "statusOfAgreement";

export enum AgreementStatus {
  SENT = "sent",
  SIGNED = "signed",
  NOT_SENT = "not-sent",
}

export interface VendorData {
  anchorName: string;
  vendorDetailsId: number;
  nakadCode: string;
  displayName: string;
  registeredName: string;
  pan: string;
  cin: string;
  address: string;
  gstin: string;
  creditPeriod: number;
  useInvoiceCreditPeriod: boolean;
  authorisedSignatoryName: string;
  authorisedSignatoryDesignation: string;
  authorisedSignatoryEmailId: string[];
  authorisedSignatoryPhoneNumber: string;
  agreementStatus: AgreementStatus;
  discountRate: number | null;
  holdBack: number | null;
  emailIdToSendCN: string[];
  vendorId: number;
  anchorId: number;
}
export default function VendorDashboard() {
  const { actor } = useContext(userContext);
  const location = useLocation<any>();
  const [openAddEditVendor, setOpenAddEditVendor] = useState<boolean>(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState<boolean>(false);
  const editVendorData = useRef<VendorData | null>(null);

  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [allVendors, setAllVendors] = useState<VendorData[]>([]);
  const [isLoadingVendors, setIsLoadingVendors] = useState<boolean>(true);
  const [appliedFilters, setAppliedFilters] = useState<Record<AllowedFilterKeysVendor, string[]>>({
    vendorName: [],
    PANNumber: [],
    statusOfAgreement: [],
  });
  const [anchorOptions, setAnchorOptions] = React.useState<{ anchorId: number; anchorName: string }[]>([]);
  const [isDownloadingReport, setIsDownloadingReport] = useState<boolean>(false);

  const columns = useMemo<MRT_ColumnDef<VendorData>[]>(
    () => [
      {
        accessorKey: "nakadCode",
        header: "NAKAD Code",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "pan",
        header: "PAN",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "displayName",
        header: "Vendor name",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "discountRate",
        header: "Discount rate",
        filterVariant: "range",
        size: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="number" />,
      },
      {
        accessorKey: "holdBack",
        header: "Holdback",
        filterVariant: "range",
        size: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        accessorKey: "emailIdToSendCN",
        header: "Email ID to send CN",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "agreementStatus",
        header: "Status of agreement",
        filterVariant: "multi-select",
        accessorFn: (row) => (row.agreementStatus ? capitalize(row.agreementStatus) : "-"),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="select" />,
      },
      {
        accessorKey: "creditPeriod",
        header: "Actual credit period",
        filterVariant: "range",
        size: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="number" />,
      },
      {
        accessorKey: "useInvoiceCreditPeriod",
        header: "Credit period calculation from master",
        size: 300,
        accessorFn: (row) => (row.useInvoiceCreditPeriod ? "Yes" : "No"),
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "registeredName",
        header: "Registered Name of the company",
        size: 240,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "cin",
        header: "CIN",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "address",
        header: "Address",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "gstin",
        header: "GSTIN",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "authorisedSignatoryName",
        header: "Authorised signatory name",
        size: 240,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "authorisedSignatoryDesignation",
        header: "Authorised signatory designation",
        size: 260,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "authorisedSignatoryEmailId",
        header: "Authorised signatory email id",
        size: 240,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        accessorKey: "authorisedSignatoryPhoneNumber",
        header: "Authorised signatory phone number",
        size: 260,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
    ],
    []
  );

  useEffect(() => {
    getAllVendors();
  }, [appliedFilters]);

  const getAllAnchors = async () => {
    useFetch(`/vendor-master/anchors`, "GET", {
      failureMessage: "Failed to fetch anchors",
      thenCallBack: (response) => {
        const anchors = response.data.result;
        setAnchorOptions(anchors);
      },
      errorCallback: (error) => {
        console.error("Error fetching anchors:", error);
      },
    });
  };

  useEffect(() => {
    getAllAnchors();
  }, []);

  const handleDeleteVendor = async () => {
    useFetch(`/vendor-master/${editVendorData.current?.anchorId}/${editVendorData.current?.vendorId}`, "DELETE", {
      thenCallBack: () => {
        setOpenDeleteConfirmation(false);
        getAllVendors();
        console.log("Vendor deleted successfully");
      },
      failureMessage: "Failed to delete vendor",
    });

    // After deletion, refresh the vendor list
  };

  const handleSubmitForm = async (data: VendorFormData) => {
    if (editVendorData.current) {
      await useFetch(`/vendor-master/${editVendorData.current.anchorId}/${editVendorData.current.vendorId}`, "PUT", {
        showSuccessToast: true,
        data: data,
        failureMessage: "Failed to update vendor",
        thenCallBack: () => {
          setOpenAddEditVendor(false);
          getAllVendors(); // Refresh the vendor list after updating
        },
      });
    } else {
      await useFetch(`/vendor-master`, "POST", {
        showSuccessToast: true,
        data: data,
        failureMessage: "Failed to add vendor",
        thenCallBack: () => {
          setOpenAddEditVendor(false);
          getAllVendors(); // Refresh the vendor list after adding
        },
      });
    }
  };

  const getAllVendors = async () => {
    setIsLoadingVendors(true);
    await useFetch(`/vendor-master/filter/apply`, "POST", {
      data: {
        vendorName: appliedFilters.vendorName || [],
        pan: appliedFilters.PANNumber || [],
        agreementStatus: appliedFilters.statusOfAgreement || [],
      },
      failureMessage: "Failed to fetch vendors",
      thenCallBack: (response) => {
        const vendors = response.data.result.vendors;
        setAllVendors(vendors);
        setIsLoadingVendors(false);
      },
      catchCallBack: () => {
        setIsLoadingVendors(false);
      },
    });
  };
  const handleDownloadReport = async () => {
    setIsDownloadingReport(true);

    await useFetch("/vendor-master/report", "POST", {
      data: {
        vendorName: appliedFilters.vendorName || [],
        pan: appliedFilters.PANNumber || [],
        agreementStatus: appliedFilters.statusOfAgreement || [],
      },
      failureMessage: "Failed to download report",
      thenCallBack: (response) => {
        downloadExcelFromBuffer(response.data.result.report, "report.xlsx");
        setIsDownloadingReport(false);
      },
      catchCallBack: () => {
        setIsDownloadingReport(false);
      },
    });
  };

  return (
    <LoggedInSkeleton
      topBarButtons={getReconTopBarButtons("Upload", actor.name, location?.state?.openCollapseOb, actor)}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        bgcolor="white"
        borderRadius={2}
        p={2}
        my={2}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h5" fontWeight={600}>
              All Vendors
            </Typography>
            {!isLoadingVendors && (
              <Chip
                label={allVendors.length}
                size="small"
                sx={{
                  bgcolor: "#551851",
                  color: "white",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  borderRadius: "16px",
                  height: "24px",
                }}
              />
            )}
          </Box>
          <Typography mt={1} color="text.secondary">
            Manage, Add, view, and delete vendors individually.
          </Typography>
        </Box>

        {/* Right: Buttons */}
        <Stack direction="row" spacing={2}>
          <CustomButton
            onClick={() => {
              editVendorData.current = null;
              setOpenAddEditVendor(true);
            }}
            variant="contained"
          >
            Add Vendor
          </CustomButton>

          <CustomButton variant="outlined" startIcon={<FilterListIcon />} onClick={() => setOpenFilter(true)}>
            Filter
          </CustomButton>

          <CustomButton variant="outlined" disabled={isDownloadingReport} onClick={() => handleDownloadReport()}>
            Download Report
          </CustomButton>
        </Stack>
      </Box>

      <ReactTable
        isLoading={isLoadingVendors}
        data={allVendors}
        columns={columns}
        enableColumnOrdering={true}
        enableRowVirtualization={true}
        enableColumnVirtualization={true}
        enableGrouping={true}
        enableRowActions={true}
        enableRowSelection={true}
        additionalInitialState={{ columnPinning: { left: ["mrt-row-select"], right: ["mrt-row-actions"] } }}
        renderRowActionMenuItems={({ row }) => [
          <MenuItem
            key="editTask"
            onClick={() => {
              editVendorData.current = row.original;
              setOpenAddEditVendor(true);
            }}
          >
            Edit
          </MenuItem>,
          <MenuItem
            key="deleteTask"
            onClick={() => {
              editVendorData.current = row.original;
              setOpenDeleteConfirmation(true);
            }}
          >
            Delete
          </MenuItem>,
        ]}
      />
      {openAddEditVendor && (
        <AddEditVendor
          open={openAddEditVendor}
          onClose={() => setOpenAddEditVendor(false)}
          onSubmit={async (vendorData) => await handleSubmitForm(vendorData)}
          initialData={editVendorData.current}
          anchorOptions={anchorOptions}
        />
      )}
      {openFilter && (
        <VendorDashboardFilter
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          setAppliedFilters={setAppliedFilters}
          appliedFilters={appliedFilters}
        />
      )}
      {openDeleteConfirmation && (
        <DeleteConfirmationModal
          open={openDeleteConfirmation}
          onClose={() => setOpenDeleteConfirmation(false)}
          onDelete={async () => {
            await handleDeleteVendor();
          }}
          title="Are You Sure?"
          description="This will delete the selected vendor permanently."
        />
      )}
    </LoggedInSkeleton>
  );
}
